<template>
  <component
    :is="componentType"
    :href="!disabled && isExternalLink ? to : null"
    :to="!disabled && !isExternalLink ? to : null"
    :class="buttonClass"
    @click="handleClick"
    :target="isExternalLink ? '_blank' : null"
    :rel="isExternalLink ? 'noopener noreferrer' : null"
  >
    <!-- Если кнопка "arrow" -->
    <slot v-if="type === 'arrow-right'">
      <!-- svg код стрелки right -->
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="80"
        height="80"
        viewBox="0 0 80 80"
        fill="none"
        class="arrow-icon"
      >
        <rect
          x="2"
          y="2"
          width="76"
          height="76"
          rx="38"
          stroke="#F51F78"
          stroke-width="4"
        />
        <path
          d="M21.3711 38.5797C26.7501 38.5797 34.0239 38.4004 39.4002 38.5797C42.8616 38.6951 53.3421 39.3171 56.7979 39.3171"
          stroke="#F51F78"
          stroke-width="4"
          stroke-linecap="round"
        />
        <path
          d="M46.1758 32.3291C47.6528 32.9967 48.971 33.8459 50.425 34.5406C51.6578 35.1297 53.0277 35.4597 54.2396 36.0808C55.8602 36.9114 57.3743 37.8517 58.7785 38.9044"
          stroke="#F51F78"
          stroke-width="4"
          stroke-linecap="round"
        />
        <path
          d="M46.7227 48.2194C46.9378 47.8281 47.3402 47.5364 47.7033 47.2452C49.6309 45.6993 51.7133 44.2938 53.7666 42.8718C55.4514 41.7051 57.6299 40.5271 58.7775 38.9043"
          stroke="#F51F78"
          stroke-width="4"
          stroke-linecap="round"
        />
      </svg>
    </slot>
    <slot v-else-if="type === 'arrow-left'">
      <!-- svg код стрелки left -->
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="80"
        height="80"
        viewBox="0 0 80 80"
        fill="none"
        class="arrow-icon"
      >
        <rect
          x="78"
          y="78"
          width="76"
          height="76"
          rx="38"
          transform="rotate(-180 78 78)"
          fill="white"
        />
        <rect
          x="78"
          y="78"
          width="76"
          height="76"
          rx="38"
          transform="rotate(-180 78 78)"
          stroke="#F45396"
          stroke-width="4"
        />
        <path
          d="M58.6289 41.4203C53.2499 41.4203 45.9761 41.5996 40.5998 41.4203C37.1384 41.3049 26.6579 40.6829 23.2021 40.6829"
          stroke="#F45396"
          stroke-width="4"
          stroke-linecap="round"
        />
        <path
          d="M33.8242 47.6709C32.3472 47.0033 31.029 46.1541 29.575 45.4594C28.3422 44.8703 26.9723 44.5403 25.7604 43.9192C24.1398 43.0886 22.6257 42.1483 21.2215 41.0956"
          stroke="#F45396"
          stroke-width="4"
          stroke-linecap="round"
        />
        <path
          d="M33.2773 31.7806C33.0622 32.1719 32.6598 32.4636 32.2967 32.7548C30.3691 34.3007 28.2867 35.7062 26.2334 37.1282C24.5486 38.2949 22.3701 39.4729 21.2225 41.0957"
          stroke="#F45396"
          stroke-width="4"
          stroke-linecap="round"
        />
      </svg>
    </slot>

    <!-- Если обычная кнопка -->
    <span>{{ text }}</span>
  </component>
</template>

<script>
export default {
  name: "CustomButton",
  props: {
    text: {
      type: String,
      required: false,
      default: "",
    },
    color: {
      type: String,
      default: "",
    },
    size: {
      type: String,
      default: "", // small, medium, large
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: "default", // default, arrow-right, arrow-left
    },
    to: {
      type: String,
      default: "",
    },
    target: {
      type: String,
      default: "_self", // Target ссылки: _self, _blank
    },
    rel: {
      type: String,
      default: "noopener noreferrer", // Атрибут rel для безопасности
    },
  },
  computed: {
    buttonClass() {
      return {
        btn: this.type !== "arrow-right" && this.type !== "arrow-left", // Только для обычных кнопок
        "arrow-icon":
          this.type === "arrow-right" ||
          this.type === "arrow-left" ||
          this.type === "disabled",
        "arrow-icon--disabled": this.disabled, // Добавьте этот класс для disabled
        [`btn-${this.color}`]: true,
        [`btn-${this.size}`]: true,
      };
    },
    isRouterLink() {
      return !!this.to && !this.isExternalLink && this.type !== "submit";
    },
    isExternalLink() {
      return /^(http|https):\/\//i.test(this.to); // Проверка на внешний адрес, учитывая и http, и https
    },
    isSubmit() {
      return this.type === "submit";
    },
    componentType() {
      if (this.isExternalLink) return "a";
      if (this.isRouterLink) return "router-link";
      if (this.isSubmit) return "button";
      return "button";
    },
  },
  methods: {
    handleClick(event) {
      if (this.disabled) {
        event.preventDefault();
        return; // Не обрабатываем отключённые кнопки
      }

      if (this.isExternalLink) return;

      if (!this.isSubmit) {
        event.preventDefault();
      }

      this.$emit("click");
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/styles.scss";

button {
  all: unset; /* Сбрасывает все стили (если поддерживается браузером) */
  background: none; /* Убирает фоновый цвет */
  border: none; /* Убирает границу */
}
span {
}

.btn {
  @include coolvetica();
  max-width: 392px;
  width: 100%;
  min-width: 150px;
  height: 87px;
  padding: 15px 15px;
  border: none;
  border-radius: 20px;
  text-decoration: none;
  cursor: pointer;
  align-items: center;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: background-color 0.3s ease; /* Плавный переход цвета фона */

  &:hover {
    background-color: $button-bgc-color-hovered;
  }
  &:active {
    background-color: $button-bgc-color-pressed;
  }
  &:disabled {
    background-color: $button-bgc-color-disabled;
  }
}

.btn-pink {
  background-color: $button-bgc-color;
  color: $white;
}
.btn-blue {
  background-color: transparent;
  border: none;
  color: $blue;
  @include manrope(600);
  font-size: 26px;
  text-transform: uppercase;
  transition: font-size 0.3s ease, color 0.3s ease;

  &:hover {
    background-color: transparent;
    color: darken($blue, 5%);
    font-size: 25px;
  }

  &:active {
    background-color: transparent;
    color: darken($blue, 10%);
    font-size: 25px;
  }
}

.btn-red {
  background-color: $red;
  color: $white;
}
.btn-small {
  font-size: 24px;
}
.btn-medium {
  font-size: 32px;
}
.btn-large {
  font-size: 45px;
}
//кнопка для телеграмм
.tg-btn {
  @include coolvetica();
  color: $black-find-tour;
  max-width: 536px;
  width: 100%;
  font-size: calc(16px + 1vw);
  min-width: 20px;
  max-height: 57px;
  // padding: 24px 24px;
  border-radius: 20px;
  cursor: pointer;
  background-color: transparent;
  border: 4px solid var(--Primary-Default, #f51f78);
  transition: border-color 0.3s ease;

  &:hover {
    background-color: transparent;
    border-color: var(--Primary-Hovered, #f40066);
  }
  &:active {
    background-color: transparent;
    border-color: var(--Primary-Pressed, #ce0056);
  }
  &:disabled {
    background-color: transparent;
    border-color: var(--Disabled, #6c6c6c);
    opacity: 0.5;
    cursor: not-allowed;
    color: #6c6c6c;
  }
}
// кнопка "выбрать тур"
.tour-btn {
  @include coolvetica();
  max-width: 536px;
  width: 100%;
  min-width: 20px;
  height: 57px;
  font-size: calc(16px + 1vw);
  // padding: 24px 24px;
  background-color: $button-bgc-color;
  color: $white;
  border-radius: 20px;
  background: var(--Primary-Default, #f51f78);
}

.arrow-icon {
  margin: 0; /* Убирает внешние отступы */
  padding: 0; /* Убирает внутренние отступы */
  border: none; /* Убирает рамку */
  background: none; /* Убирает фоновый цвет */
  svg {
    transition: stroke 0.3s ease;
    fill: $button-bgc-color;
  }
  rect {
    // transition: stroke 0.3s ease;
    stroke: $button-bgc-color;
  }
  path {
    // transition: stroke 0.3s ease;
    stroke: $white;
  }

  &:hover {
    svg {
      fill: $button-bgc-color-hovered;
    }
    rect {
      stroke: $button-bgc-color-hovered;
    }
    path {
      stroke: $white;
    }
  }

  &:active {
    svg {
      fill: $button-bgc-color-pressed;
    }
    rect {
      stroke: $button-bgc-color-pressed;
    }
    path {
      stroke: $white;
    }
  }
}
.arrow-icon--disabled {
  cursor: not-allowed; // Меняем курсор
  svg {
    fill: none;
  }
  rect {
    stroke: $button-bgc-color;
  }
  path {
    stroke: $button-bgc-color;
  }
  &:hover {
    svg {
      fill: none;
    }
    rect {
      stroke: $button-bgc-color-hovered;
    }
    path {
      stroke: $button-bgc-color-hovered;
    }
  }
  &:active {
    svg {
      fill: none;
    }
    rect {
      stroke: $button-bgc-color-pressed;
    }
    path {
      stroke: $button-bgc-color-pressed;
    }
  }
}
</style>
